// LockScreen — unlock form for returning users.
// Shown when `isEnabled && isConfigured && !isUnlocked`.

import { useState, useRef, useEffect, type FormEvent } from 'react';
import { useAppLock } from './AppLockContext';

export function LockScreen() {
  const { lock } = useAppLock();
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!lock) return;
    setBusy(true);
    try {
      const ok = await lock.unlock(password);
      if (!ok) {
        setError('Incorrect password. Please try again.');
        setPassword('');
        inputRef.current?.focus();
      }
      // On success the `unlock` event flips isUnlocked; AppGate renders the app.
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="applock-screen" role="dialog" aria-modal="true">
      <form className="applock-card" onSubmit={onSubmit}>
        <h1 className="applock-title">App Locked</h1>
        <p className="applock-lead">Enter your password to continue.</p>
        <div className="form-group">
          <label htmlFor="applock-unlock">Password</label>
          <input
            id="applock-unlock"
            ref={inputRef}
            type="password"
            className="input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            disabled={busy}
            required
          />
        </div>
        {error && (
          <p className="applock-error" role="alert">
            {error}
          </p>
        )}
        <div className="applock-actions">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={busy || !password}
          >
            {busy ? 'Unlocking…' : 'Unlock'}
          </button>
        </div>
      </form>
    </div>
  );
}
